// Spares Audit — shared metadata and small pure helpers.
// Business rules live on the server (docs/BACKEND-REQUEST-spares-audit.md);
// this file only describes how to SHOW what the server returns.

export const MODULE = 'SparesAudit'

// Audit status of a voucher, derived from the server's `audit` block.
//  NEW      - never audited (no audit row), or an audit that was reopened
//  CHANGED  - audited, but the voucher was edited afterwards (hash mismatch)
//  QUERY    - a query is open with the store
//  AUDITED  - audited and unchanged since
export const STATUS_META = {
  NEW: { label: 'New', tile: 'New', cls: 'new' },
  CHANGED: { label: 'Edited since audit', tile: 'Edited since audit', cls: 'changed' },
  QUERY: { label: 'Query', tile: 'Query raised', cls: 'query' },
  AUDITED: { label: 'Audited', tile: 'Audited', cls: 'audited' }
}

export const statusOf = (item) => {
  const a = item && item.audit
  if (!a || a.status === 'REOPENED') return 'NEW'
  if (a.status === 'QUERY') return 'QUERY'
  if (a.changedSinceAudit) return 'CHANGED'
  return 'AUDITED'
}

// Check codes computed by the server. Labels only - severity always comes from
// the server's `severity` field. An unknown code is still shown (raw code), never hidden.
export const CHECK_META = {
  NO_SPARE_LINES: { label: 'No spare lines', short: 'No lines' },
  BLANK_LINE: { label: 'Blank line', short: 'Blank' },
  UOM_MISMATCH: { label: 'Unit differs from spare master', short: 'UoM' },
  DUPLICATE_VENDOR_INVOICE: { label: 'Same vendor invoice twice in a financial year', short: 'Dup' },
  ISSUE_EXCEEDS_STOCK: { label: 'Issued more than store stock', short: 'Stock' },
  RATE_NOT_NUMERIC: { label: 'Rate is not a number', short: 'Rate?' },
  RATE_JUMP: { label: 'Rate differs from last purchase', short: 'Rate' },
  NO_ATTACHMENT: { label: 'Invoice entered but not attached', short: 'No file' },
  INVOICE_NO_MISSING: { label: 'Vendor invoice no. missing', short: 'Inv no' },
  FRACTIONAL_COUNT: { label: 'Fractional count', short: 'Fraction' },
  QTY_ABOVE_USUAL: { label: 'Quantity above usual', short: 'Qty' },
  // INVOICE_NO_MISSING / INVOICE_DATE_MISSING are no longer raised (a missing invoice number or
  // date means "pending invoice"); labels kept for audit history recorded before the change.
  INVOICE_DATE_MISSING: { label: 'Invoice date missing', short: 'Inv date' },
  // Not a check: filter value for PIs whose invoice is still pending.
  PENDING_INVOICE: { label: 'Invoice pending', short: 'Pending' },
  STORE_NOT_RECORDED: { label: 'Store location not recorded', short: 'Store?' },
  NOT_IN_LEDGER: { label: 'Not in stock ledger', short: 'Ledger' },
  FUTURE_DATE: { label: 'Dated in the future', short: 'Future' }
}
export const CHECK_ORDER = Object.keys(CHECK_META)
// Check-type names come from the server (queue.checkTypes), so filter chips, the checks on each
// voucher and line, the query window, history, log, export and Audit Query alerts all use exactly
// the same name (Rishabh, 2026-10-02). CHECK_META is only the fallback before the first load.
const SERVER_CHECK_LABELS = {}
let SERVER_CHECK_ORDER = []
export const setCheckTypes = (list) => {
  if (!Array.isArray(list) || !list.length) return
  SERVER_CHECK_ORDER = list.map(t => t.code)
  list.forEach(t => { if (t && t.code && t.label) SERVER_CHECK_LABELS[t.code] = t.label })
}
export const checkOrder = () => (SERVER_CHECK_ORDER.length ? SERVER_CHECK_ORDER : CHECK_ORDER)
export const checkLabel = (code) => SERVER_CHECK_LABELS[code] || (CHECK_META[code] ? CHECK_META[code].label : code)
// Kept for older imports: shows the full check-type name, never an abbreviation.
export const checkShort = checkLabel
export const sevCls = (severity) => (severity === 'CRITICAL' ? 'crit' : severity === 'WARNING' ? 'warn' : 'neutral')

export const allChecks = (item) => {
  const out = (item.checks || []).map(c => ({ ...c, lineNo: null }))
  ;(item.lines || []).forEach((ln, i) => (ln.checks || []).forEach(c => out.push({ ...c, lineNo: i + 1 })))
  return out
}

// Bulk audit is limited to vouchers that were never audited and have no checks
// at all. Edited, queried or flagged vouchers are audited one by one. The server
// enforces the same rule; this only keeps the UI honest about what will happen.
export const bulkEligible = (item) => statusOf(item) === 'NEW' && allChecks(item).length === 0

// Legacy: one reason per line, written by builds before 2026-10-08 (local test data only).
// Kept so old history entries still read; new queries use FLAG_FIELDS.
export const LINE_REASONS = [
  { key: 'WRONG_SPARE', label: 'Wrong spare' },
  { key: 'WRONG_UOM', label: 'Wrong unit' },
  { key: 'WRONG_QTY', label: 'Wrong quantity' },
  { key: 'WRONG_RATE', label: 'Wrong rate' },
  { key: 'OTHER', label: 'Other' }
]
export const reasonLabel = (k) => { const r = LINE_REASONS.find(x => x.key === k); return r ? r.label : k }

// What the auditor can flag (Rishabh, 2026-10-08): any field the store enters on the PI / SPI form,
// at voucher level or on a spare line, several per line. "Other" needs a remark. Same list as the
// server (spare-audit.model.ts FLAG_FIELDS), which rejects anything else - change both together.
const LINE_FIELDS = [['SPARE', 'Spare'], ['UNIT', 'Unit'], ['QTY', 'Quantity'], ['RATE', 'Rate'], ['STORE', 'Store / sub-location'], ['OTHER', 'Other']]
export const FLAG_FIELDS = {
  PI: {
    VOUCHER: [['VENDOR', 'Vendor'], ['INVOICE_NO', 'Vendor invoice no.'], ['INVOICE_DATE', 'Invoice date'], ['GRN_NO', 'Manual GRN no.'],
      ['GRN_DATE', 'GRN date'], ['ORDER_NO', 'Order no.'], ['ORDER_DATE', 'Order date'], ['OWNER', 'Owner'], ['ATTACHMENTS', 'Attachments'], ['OTHER', 'Other']],
    LINE: LINE_FIELDS
  },
  SPI: {
    VOUCHER: [['JOB_CARD', 'Job card'], ['ISSUE_DATE', 'Issue date'], ['OTHER', 'Other']],
    LINE: LINE_FIELDS
  }
}
export const flagFields = (voucherType, lineId) => {
  const f = FLAG_FIELDS[voucherType] || FLAG_FIELDS.PI
  return (lineId ? f.LINE : f.VOUCHER).map(([key, label]) => ({ key, label }))
}
export const flagLabel = (voucherType, lineId, field) => {
  const hit = flagFields(voucherType, lineId).find(x => x.key === field)
  return hit ? hit.label : field
}
// Flags of one voucher are kept as { key: { lineId, field } }; key = lineId (or '') + '|' + field.
export const flagKey = (lineId, field) => (lineId || '') + '|' + field

// Pre-select the field from the line's own checks when a line is first flagged; the auditor can add more.
export const suggestField = (line) => {
  const codes = (line.checks || []).map(c => c.code)
  if (codes.indexOf('UOM_MISMATCH') >= 0) return 'UNIT'
  if (codes.indexOf('RATE_JUMP') >= 0 || codes.indexOf('RATE_NOT_NUMERIC') >= 0) return 'RATE'
  if (codes.some(c => c === 'ISSUE_EXCEEDS_STOCK' || c === 'QTY_ABOVE_USUAL' || c === 'FRACTIONAL_COUNT')) return 'QTY'
  if (codes.indexOf('STORE_NOT_RECORDED') >= 0) return 'STORE'
  return 'SPARE'
}

// The value of a voucher field as entered (for the query window); null = not entered.
export const voucherFieldValue = (item, field, moment) => {
  const day = (d) => fmtDate(d, moment)
  switch (field) {
    case 'VENDOR': return item.vendor ? item.vendor.name : null
    case 'INVOICE_NO': return (item.vendorInvoiceNumber || '').trim() || null
    case 'INVOICE_DATE': return day(item.invoiceDate)
    case 'GRN_NO': return item.grnNo || null
    case 'GRN_DATE': case 'ISSUE_DATE': return day(item.date)
    case 'ORDER_NO': return item.orderNumber || null
    case 'ORDER_DATE': return day(item.orderDate)
    case 'OWNER': return item.owner ? item.owner.name : null
    case 'ATTACHMENTS': return (item.documents || []).length + ' attached'
    case 'JOB_CARD': return item.jobCard ? item.jobCard.no : null
    default: return null
  }
}

// Alert statuses (alerts collection, as used by the web app's alert inbox).
export const ALERT_STATUS = {
  NEW: 'New', UNDER_ANALYSIS: 'Under analysis', ACTION_PENDING: 'Action pending', ACTION_TAKEN: 'Action taken',
  CLOSED: 'Closed', IGNORED: 'Ignored', DIRECT_ACTION: 'Direct action'
}
export const alertStatusLabel = (s) => ALERT_STATUS[s] || s || 'Unknown'

export const EVENT_META = {
  AUDITED: { label: 'Audited', color: 'var(--match)' },
  QUERY_RAISED: { label: 'Query raised', color: 'var(--warn)' },
  REOPENED: { label: 'Audit reopened', color: 'var(--accent)' },
  CHANGED_AFTER_AUDIT: { label: 'Edited after audit', color: 'var(--dup)' }
}

export const CHANGE_KIND = {
  LINE_ADDED: 'Line added',
  LINE_REMOVED: 'Line removed',
  LINE_CHANGED: 'Line changed',
  HEADER_CHANGED: 'Voucher changed',
  DOCUMENTS_CHANGED: 'Attachments changed'
}

// ---- formatting: null/undefined is MISSING and is never shown as 0 ----
export const isNum = (n) => typeof n === 'number' && isFinite(n)
export const inr = (n) => (isNum(n)
  ? '₹' + n.toLocaleString('en-IN', { minimumFractionDigits: n % 1 ? 2 : 0, maximumFractionDigits: 2 })
  : null)
export const qtyFmt = (n) => (isNum(n) ? n.toLocaleString('en-IN', { maximumFractionDigits: 3 }) : null)

export const fmtDate = (d, moment) => (d ? moment(d).format('D MMM YYYY') : null)
export const fmtDateTime = (d, moment) => (d ? moment(d).format('D MMM YYYY, HH:mm') : null)

export const isImage = (doc) => {
  const mt = (doc && doc.mimeType) || ''
  if (mt) return mt.indexOf('image/') === 0
  return /\.(jpe?g|png|gif|webp|bmp)$/i.test((doc && (doc.url || doc.name)) || '')
}

// ---- permissions ----
// Merges access across all of the user's roles, like RolesGuard does on the server.
export const permissionsFor = (authUser, resource) => {
  if (!authUser) return []
  return (authUser.roles || []).reduce((acc, role) => {
    ;((role && role.access) || []).forEach(a => {
      if (a && a.name === resource && Array.isArray(a.permissions)) acc.push(...a.permissions)
    })
    return acc
  }, [])
}
// Spares Audit is reachable only through role permissions (Rishabh, 2026-10-02):
// no super-admin shortcut. The server enforces the same rule.
export const can = (authUser, verb) => {
  if (!authUser) return false
  const p = permissionsFor(authUser, MODULE)
  return verb === 'any' ? p.length > 0 : p.indexOf(verb) >= 0
}

// What a user may open from Spares Audit in another module (links to PIs, spares, job cards):
// 'edit' with update permission, 'view' with read only, null with neither (shown as plain text).
// Super-admins get 'edit': those modules' own guards let super-admins through.
export const recordAccess = (authUser, resource) => {
  if (!authUser) return null
  if (authUser.superAdmin) return 'edit'
  const p = permissionsFor(authUser, resource)
  if (p.indexOf('u') >= 0) return 'edit'
  if (p.indexOf('r') >= 0) return 'view'
  return null
}

export const newRequestId = () => 'sa-' + Date.now().toString(36) + '-' + Math.random().toString(36).slice(2, 8)
